<template>
    <div
        class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100 hover:border-gray-200 group">
        <!-- Header Section -->
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
            <!-- Title and Badges -->
            <div class="flex-1 min-w-0">
                <h4 class="font-semibold text-gray-900 text-base lg:text-lg mb-3 leading-tight">{{ activity.title }}
                </h4>

                <!-- Badges Row -->
                <div class="flex flex-wrap items-center gap-2 mb-3">
                    <!-- General Activity Badge -->
                    <span v-if="activity.isGeneralActivity"
                        class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <font-awesome-icon icon="fa-solid fa-globe" class="mr-1.5 text-xs"></font-awesome-icon>
                        General
                    </span>

                    <!-- Department/EventType Badge -->
                    <span class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium max-w-full"
                        :class="departmentBadgeClass">
                        <span class="truncate">{{ getShortenedEventType(activity.department || activity.eventType)
                        }}</span>
                    </span>

                    <!-- Entity Badge -->
                    <span class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium"
                        :class="entityBadgeClass">
                        {{ activity.entity }}
                    </span>
                </div>
            </div>

            <!-- Date Section -->
            <div class="flex-shrink-0 text-right">
                <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div class="text-sm font-semibold text-gray-900">{{ formatDateRange(activity.startDate,
                        activity.endDate) }}</div>
                    <div class="text-xs text-gray-500 mt-1">{{ formatTime(activity.startDate, activity.endDate) }}</div>
                </div>
            </div>
        </div>

        <!-- Description -->
        <p class="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">{{ activity.description }}</p>

        <!-- Details Section -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Location -->
            <div v-if="activity.location && activity.location !== 'NA'" class="flex items-start gap-2">
                <div class="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                    <font-awesome-icon icon="fa-solid fa-map-marker-alt"
                        class="text-xs text-blue-600"></font-awesome-icon>
                </div>
                <div class="min-w-0">
                    <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Location</div>
                    <div class="text-sm text-gray-700 leading-tight">{{ activity.location }}</div>
                </div>
            </div>

            <!-- Time -->
            <div v-if="activity.startTime && activity.endTime" class="flex items-start gap-2">
                <div class="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <font-awesome-icon icon="fa-solid fa-clock" class="text-xs text-purple-600"></font-awesome-icon>
                </div>
                <div class="min-w-0">
                    <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Time</div>
                    <div class="text-sm text-gray-700 leading-tight">{{ formatTimeRange(activity.startTime,
                        activity.endTime) }}</div>
                </div>
            </div>

            <!-- Target Audience -->
            <div v-if="activity.targetAudience" class="flex items-start gap-2">
                <div class="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                    <font-awesome-icon icon="fa-solid fa-users" class="text-xs text-green-600"></font-awesome-icon>
                </div>
                <div class="min-w-0">
                    <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Target Audience</div>
                    <div class="text-sm text-gray-700 leading-tight">{{ activity.targetAudience }}</div>
                </div>
            </div>
        </div>

        <!-- Duration Badge -->
        <div v-if="formatDuration(activity.startDate, activity.endDate)" class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between">
                <span class="text-xs text-gray-500">Duration</span>
                <span
                    class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {{ formatDuration(activity.startDate, activity.endDate) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "activities-calendar-card",
    props: {
        activity: {
            type: Object,
            required: true
        }
    },
    computed: {
        entityBadgeClass() {
            const classMap = {
                'ADPOLY': 'bg-orange-100 text-orange-800 border border-orange-200',
                'FCHS': 'bg-amber-100 text-amber-800 border border-amber-200',
                'ATS': 'bg-rose-100 text-rose-800 border border-rose-200'
            };
            return classMap[this.activity.entity] || 'bg-gray-100 text-gray-800 border border-gray-200';
        },
        departmentBadgeClass() {
            // Different colors for different departments/eventTypes - using cool colors
            const departmentColors = {
                'Emergency Health': 'bg-red-100 text-red-800 border border-red-200',
                'RMI': 'bg-pink-100 text-pink-800 border border-pink-200',
                'RMI and Nursing': 'bg-rose-100 text-rose-800 border border-rose-200',
                'Psychology and Nursing': 'bg-violet-100 text-violet-800 border border-violet-200',
                'Nursing': 'bg-lime-100 text-lime-800 border border-lime-200',
                'Midwifery': 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200',
                'Radiography': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
                'Physiotherapy': 'bg-teal-100 text-teal-800 border border-teal-200',
                'Pharmacy': 'bg-sky-100 text-sky-800 border border-sky-200',
                'Pharmacy and Nursing': 'bg-cyan-100 text-cyan-800 border border-cyan-200',
                'All': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'Psychology': 'bg-violet-100 text-violet-800 border border-violet-200',
                'ADPOLY General Events': 'bg-blue-100 text-blue-800 border border-blue-200',
                'ADPOLY General Events/New Ss': 'bg-blue-100 text-blue-800 border border-blue-200',
                'Academic Sessions': 'bg-slate-100 text-slate-800 border border-slate-200',
                'Specialized Departmental Training and Events': 'bg-cyan-100 text-cyan-800 border border-cyan-200',
                'Field Trips/Participation': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'Field Trip': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'Field Trips': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'Field Trip/Participation/PET': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'Open Mic Discussion': 'bg-violet-100 text-violet-800 border border-violet-200',
                'Academic Sessions/Staff': 'bg-slate-100 text-slate-800 border border-slate-200',
                'Competition': 'bg-red-100 text-red-800 border border-red-200',
                'Competition/ISET': 'bg-red-100 text-red-800 border border-red-200',
                'Field Trip/ISET': 'bg-emerald-100 text-emerald-800 border border-emerald-200'
            };
            const displayValue = this.activity.department || this.activity.eventType;
            return departmentColors[displayValue] || 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    },
    methods: {
        getShortenedEventType(eventType) {
            if (!eventType) return '';

            // Shorten long event type names
            const shortenedNames = {
                'ADPOLY General Events': 'ADPOLY General',
                'ADPOLY General Events/New Ss': 'ADPOLY General/New',
                'Specialized Departmental Training and Events': 'Specialized Training',
                'Field Trips/Participation': 'Field Trips',
                'Field Trip/Participation/PET': 'Field Trip/PET',
                'Academic Sessions/Staff': 'Academic/Staff',
                'Competition/ISET': 'Competition/ISET',
                'Field Trip/ISET': 'Field Trip/ISET'
            };

            return shortenedNames[eventType] || eventType;
        },
        formatTimeRange(startTime, endTime) {
            if (!startTime || !endTime) return '';

            // Handle "All Day" case (00:00:00 to 23:59:59)
            if (startTime === '00:00:00' && endTime === '23:59:59') {
                return 'All Day';
            }

            // Format start and end times
            const startFormatted = this.formatSingleTime(startTime);
            const endFormatted = this.formatSingleTime(endTime);

            return `${startFormatted} - ${endFormatted}`;
        },
        formatSingleTime(timeString) {
            if (!timeString) return '';

            // Handle time like "12:00:00"
            const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1]);
                const minutes = timeMatch[2];
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                return `${displayHours}:${minutes} ${ampm}`;
            }

            return timeString;
        },
        formatDuration(startDate, endDate) {
            if (startDate === null || endDate === null) {
                return '';
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 0) {
                return 'Single day';
            } else if (diffDays === 1) {
                return '1 day';
            } else if (diffDays < 7) {
                return `${diffDays} days`;
            } else if (diffDays < 30) {
                const weeks = Math.ceil(diffDays / 7);
                return `${weeks} week${weeks > 1 ? 's' : ''}`;
            } else {
                const months = Math.ceil(diffDays / 30);
                return `${months} month${months > 1 ? 's' : ''}`;
            }
        },
        formatDateRange(startDate, endDate) {
            // Handle TBD dates
            if (startDate === null || endDate === null) {
                return 'TBD';
            }

            const start = new Date(startDate);
            const end = new Date(endDate);

            if (startDate === endDate) {
                return start.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
            } else {
                const startStr = start.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                const endStr = end.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
                return `${startStr} - ${endStr}`;
            }
        },
        formatTime(startDate, endDate) {
            // For activities, we don't have specific times, so we'll show "All Day" for multi-day events
            // and "Single Day" for single day events
            if (startDate === null || endDate === null) {
                return '';
            } else if (startDate === endDate) {
                return 'Single Day Event';
            } else {
                return 'Multi-Day Event';
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
